import { useState, useEffect, useCallback, memo } from "react";
import { useTranslation } from "../contexts/TranslationContext";
import { Globe } from "lucide-react";

/**
 * TranslatableMessage - Translates message content if auto-translate is enabled
 * Shows only the translated text (or original if translation fails/disabled)
 */
const TranslatableMessage = memo(({ content, renderContent, customStyle = {} }) => {
  const { settings, translateText } = useTranslation();
  const [translatedContent, setTranslatedContent] = useState(content);
  const [isTranslating, setIsTranslating] = useState(false);
  const [wasTranslated, setWasTranslated] = useState(false);

  const performTranslation = useCallback(async () => {
    // Don't translate if disabled or content is very short
    if (!settings.auto_translate || !content || content.length < 3) {
      setTranslatedContent(content);
      setWasTranslated(false);
      return;
    }

    // Skip translation for content that looks like URLs, emojis, or smiley codes
    if (content.match(/^https?:\/\//i) || content.match(/^\{[a-zA-Z0-9_]+\}$/)) {
      setTranslatedContent(content);
      setWasTranslated(false);
      return;
    }

    setIsTranslating(true);
    try {
      const result = await translateText(content);
      setTranslatedContent(result.translated || content);
      setWasTranslated(result.wasTranslated || false);
    } catch (error) {
      console.error("Translation failed:", error);
      setTranslatedContent(content);
      setWasTranslated(false);
    } finally {
      setIsTranslating(false);
    }
  }, [content, settings.auto_translate, translateText]);

  useEffect(() => {
    performTranslation();
  }, [performTranslation]);

  // If we have a custom render function, use it
  if (renderContent) {
    return (
      <span className="inline">
        {renderContent(translatedContent, customStyle)}
        {wasTranslated && (
          <Globe className="inline-block w-3 h-3 ml-1 text-blue-400 opacity-60" title="Translated" />
        )}
        {isTranslating && (
          <span className="inline-block w-3 h-3 ml-1 animate-pulse text-blue-400" title="Translating...">
            ...
          </span>
        )}
      </span>
    );
  }

  // Default rendering
  return (
    <span className="inline" style={customStyle}>
      {translatedContent}
      {wasTranslated && (
        <Globe className="inline-block w-3 h-3 ml-1 text-blue-400 opacity-60" title="Translated" />
      )}
      {isTranslating && (
        <span className="inline-block ml-1 animate-pulse text-blue-400" title="Translating...">
          ...
        </span>
      )}
    </span>
  );
});

TranslatableMessage.displayName = "TranslatableMessage";

export default TranslatableMessage;
